import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'

import useIsMountedRef from 'use-is-mounted-ref'

import { IMsalContext, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { AccountInfo, AuthenticationResult, BrowserAuthError, BrowserAuthErrorCodes } from '@azure/msal-browser'

import { Authentication, MicrosoftSSO as MicrosoftSSOLanguage } from '../../../../../i18n'

import { LandingPageProps } from './Types/Types'
import { LoginState } from '../LoginForm/Constants/Constants'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'

import { LanguageContext, PromptContext, ReactState } from '../../../../React/Common/Contexts'
import { displayErrorMessage } from '../../../../React/Common/Utils/Messages'
import { RESTAPIError } from '../../../../React/Common/API'
import { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import { signInWithMsal, SsoLoginResponse } from '../../../../React/Common/API/UserSignInOutAPI'
import { StateSetter } from '../../../../React/Common/Types/Types'

import { redirectToServlet, ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { StatusCodes } from '../../../../NonReact/constants/StatusCodes'
import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { APIData, JSONResponse } from '../../../../NonReact/common/Types/Types'

type Props = {
    setIsLoading: StateSetter<boolean>
    landingPage?: LandingPageProps
    resetForm?: () => void
}

const HASH_KEY: string = 'msso'
const BROWSER_AUTH_ERRORCODE_ACCESS_DENIED: string = 'access_denied'
const prompt: string = 'select_account'

export const MicrosoftSSO: FC<Props> = ({ setIsLoading, landingPage, resetForm }): ReactElement => {
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(MicrosoftSSOLanguage.NAME)

    const { instance, accounts }: IMsalContext = useMsal()
    const isAuthenticated: boolean = useIsAuthenticated()
    const [isButtonDisabled, setIsButtonDisabled]: ReactState<boolean> = useState<boolean>(isAuthenticated)

    const clientId: string = instance.getConfiguration()?.auth?.clientId
    const scopes: string[] = [`${clientId}/.default`, 'openid', 'profile']

    const handleRedirect = (): void => {
        redirectToServlet(ServletCmds.MAIN_HOME)
    }

    const handleAkixiAuthentication = ({ accessToken, account }: AuthenticationResult): void => {
        if (isMounted.current) {
            setIsLoading(true)
            signInWithMsal(accessToken)
                .then((response: JSONResponse<SsoLoginResponse>): void => {
                    if (!response?.otherSessionsClosed) {
                        handleRedirect()
                        return
                    }

                    displayPromptPromise(
                        <div>
                            <h2>{language.get(MicrosoftSSOLanguage.MicrosoftSSO_Session_Ended_Title)}</h2>
                            <p>
                                {language.getInterpolated(
                                    MicrosoftSSOLanguage.MicrosoftSSO_Session_Ended_Text,
                                    account.username
                                )}
                            </p>
                        </div>,
                        undefined,
                        undefined,
                        undefined,
                        true
                    ).then((): void => handleRedirect())
                })
                .catch(handleSignInError)
        }
    }

    const handleSignInError = (error: RESTAPIError): void => {
        if (isMounted.current) {
            setIsButtonDisabled(false)
            setIsLoading(false)
            instance?.clearCache()

            displayErrorMessage(
                language.get(
                    error.HTTPStatus === StatusCodes.NOT_FOUND
                        ? MicrosoftSSOLanguage.MicrosoftSSO_Login_Error
                        : MicrosoftSSOLanguage.MicrosoftSSO_MissingCode_Error
                )
            )
        }
    }

    const isLoginLandingPageLoginPageView: boolean =
        isVarNotEmpty(landingPage) && landingPage.isEnabled && landingPage.loginView === LoginState.VIEW_USERNAME

    const nonAdminErrorHandler = (): void => {
        landingPage?.setValues(EMPTY_STRING, EMPTY_STRING)
        instance?.clearCache()
    }

    const handleLogin = (): void => {
        resetForm?.()
        setIsButtonDisabled(true)
        setIsLoading(true)

        instance
            .loginPopup({ scopes, prompt })
            .then((authResult: AuthenticationResult) => {
                if (!authResult?.account) {
                    displayErrorMessage(language.get(MicrosoftSSOLanguage.MicrosoftSSO_MissingCode_Error))
                    return
                }

                const { account } = authResult

                if (!isLoginLandingPageLoginPageView) {
                    instance.setActiveAccount(account)
                    return
                }

                const { homeAccountId, environment, tenantId, localAccountId, username, idToken }: AccountInfo = account
                const ssoHashString: string = `#${HASH_KEY}=${btoa(
                    JSON.stringify({
                        homeAccountId,
                        environment,
                        tenantId,
                        username,
                        localAccountId,
                        idToken
                    })
                )}`

                landingPage?.setValues(account.username, ssoHashString)
                landingPage
                    ?.performCheck(account.username)
                    .then((response: Nullable<APIData<{ url: string }>>): void => {
                        const url: string = URLConstructor.newInstance({
                            baseURL: response?.Data.url,
                            pathParams: [window.location.pathname],
                            queryParams: {
                                username: account.username,
                                view: LoginState.VIEW_PASSWORD
                            }
                        }).toString()

                        window.location.assign(`${url}${ssoHashString}`)
                    })
                    .catch((error: RESTAPIError): void => {
                        if (error.HTTPStatus === StatusCodes.NOT_FOUND) {
                            displayErrorMessage(
                                language.get(Authentication.LoginForm_LoginLanding_User_Not_Found_Error)
                            )

                            if (isMounted.current) {
                                setIsButtonDisabled(false)
                                setIsLoading(false)
                            }

                            return
                        }

                        landingPage.performLookup(account.username, nonAdminErrorHandler).then((): void => {
                            if (isMounted.current) {
                                setIsButtonDisabled(false)
                                setIsLoading(false)
                            }
                        })
                    })
            })
            .catch((err: BrowserAuthError): void => {
                if (isMounted.current) {
                    setIsButtonDisabled(false)
                    setIsLoading(false)
                }

                if (
                    err.errorCode !== BrowserAuthErrorCodes.userCancelled &&
                    err.errorCode !== BROWSER_AUTH_ERRORCODE_ACCESS_DENIED
                ) {
                    displayErrorMessage(language.get(MicrosoftSSOLanguage.MicrosoftSSO_MissingCode_Error))
                }
            })
    }

    useEffect((): void => {
        if (isLoginLandingPageLoginPageView) return
        if (!accounts.length) return
        const account: AccountInfo = accounts[accounts.length - 1]
        instance.initialize().then((): void => {
            instance.acquireTokenSilent({ scopes, account }).then(handleAkixiAuthentication).catch(handleSignInError)
        })
    }, [accounts])

    useEffect((): void => {
        if (window.location.hash) {
            const formattedHash: string = window.location.hash.replace(`#${HASH_KEY}=`, EMPTY_STRING)
            if (!formattedHash) return
            history.replaceState(EMPTY_STRING, document.title, window.location.pathname + window.location.search)

            try {
                const decodedHash: string = atob(formattedHash)
                const account: AccountInfo = JSON.parse(decodedHash) as AccountInfo

                setIsLoading(true)
                instance.initialize().then((): void => {
                    instance
                        .acquireTokenSilent({ scopes, account })
                        .then(handleAkixiAuthentication)
                        .catch(handleSignInError)
                })
            } catch {
                displayErrorMessage(language.get(MicrosoftSSOLanguage.MicrosoftSSO_MissingCode_Error))
            }
        }
    }, [])

    return (
        <div className='router-microsoft-sso'>
            <RoundedButton className='rsiw-sso-button' disabled={isButtonDisabled} onClick={handleLogin}>
                <div className='rsiwsb-logo-container'>
                    <img
                        className='rsiwsb-logo'
                        src='/CCS/Styles/Images/Akixi2019/microsoft-logo.png'
                        alt='Microsoft logo'
                    />
                </div>
                <span>{language.get(MicrosoftSSOLanguage.MicrosoftSSO_Button_Title)}</span>
            </RoundedButton>
        </div>
    )
}
